import { useRef, useState } from "react";
import { motion, useInView } from "motion/react";
import { ArrowRight, Rocket } from "lucide-react";
import { siteData } from "../../data/mock";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const spring = { type: "spring", damping: 80, stiffness: 400, mass: 1 };

/* ========== Final CTA Section ========== */
export const CTASection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { finalCta } = siteData;
  const [form, setForm] = useState({ name: "", email: "", business: "" });
  const [showForm, setShowForm] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.name && form.email) {
      try {
        await axios.post(`${BACKEND_URL}/api/demo-requests`, {
          name: form.name,
          email: form.email,
          business: form.business,
        });
        toast.success("Demo booked! We'll reach out within 24 hours.");
        setForm({ name: "", email: "", business: "" });
        setShowForm(false);
      } catch {
        toast.error("Something went wrong. Please try again.");
      }
    }
  };

  return (
    <section
      ref={ref}
      id="final-cta"
      className="py-20 md:py-28 relative overflow-hidden"
    >
      {/* Gradient BG */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, rgba(217,138,140,0.12) 0%, rgba(225,197,103,0.1) 50%, rgba(223,181,115,0.08) 100%)",
        }}
      />

      <div className="relative z-10 max-w-[700px] mx-auto px-6 md:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...spring }}
        >
          <div className="w-16 h-16 rounded-2xl bg-[#008055]/10 flex items-center justify-center mx-auto mb-6">
            <Rocket size={28} className="text-[#008055]" />
          </div>

          <h2
            className="text-[#0A0A0A] leading-tight tracking-tight mb-4"
            style={{ fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 500 }}
          >
            {finalCta.headline}
            <span className="text-[#008055]">{finalCta.headlineAccent}</span>
          </h2>

          <p className="text-[#3C3B3D] text-lg leading-relaxed mb-10 max-w-[500px] mx-auto">
            Join hundreds of businesses using P2Points to grow their customer loyalty and drive repeat visits.
          </p>
        </motion.div>

        {!showForm ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ ...spring, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setShowForm(true)}
              className="bg-[#008055] hover:bg-[#0A6647] text-white border-none rounded-full px-8 h-[52px] text-base font-normal cursor-pointer flex items-center justify-center gap-2 transition-colors duration-200"
            >
              {finalCta.ctaPrimary}
              <ArrowRight size={18} />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setShowForm(true)}
              className="bg-white hover:bg-[#F0EDF5] text-[#0A0A0A] border border-[#CBC9CF] rounded-full px-8 h-[52px] text-base font-normal cursor-pointer flex items-center justify-center gap-2 transition-colors duration-200"
            >
              {finalCta.ctaSecondary}
            </motion.button>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...spring }}
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl p-6 md:p-8 shadow-lg border border-[#E6E4EB] text-left max-w-[480px] mx-auto"
          >
            <h3 className="text-[#0A0A0A] text-lg font-medium mb-5">Book Your Demo</h3>
            <div className="space-y-4 mb-6">
              <input
                type="text"
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full h-12 px-4 border border-[#E6E4EB] rounded-xl text-base bg-white text-[#0A0A0A] outline-none focus:border-[#008055] placeholder:text-[#AFADB2] transition-colors duration-200"
                required
              />
              <input
                type="email"
                placeholder="Business email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full h-12 px-4 border border-[#E6E4EB] rounded-xl text-base bg-white text-[#0A0A0A] outline-none focus:border-[#008055] placeholder:text-[#AFADB2] transition-colors duration-200"
                required
              />
              <input
                type="text"
                placeholder="Business name (optional)"
                value={form.business}
                onChange={(e) => setForm({ ...form, business: e.target.value })}
                className="w-full h-12 px-4 border border-[#E6E4EB] rounded-xl text-base bg-white text-[#0A0A0A] outline-none focus:border-[#008055] placeholder:text-[#AFADB2] transition-colors duration-200"
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="flex-1 bg-[#008055] hover:bg-[#0A6647] text-white border-none rounded-full h-12 text-base font-normal cursor-pointer transition-colors duration-200"
              >
                Submit Request
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-5 bg-transparent hover:bg-[#F0EDF5] text-[#3C3B3D] border border-[#CBC9CF] rounded-full h-12 text-sm cursor-pointer transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </motion.form>
        )}
      </div>
    </section>
  );
};

/* ========== Footer ========== */
export const Footer = () => {
  const { footer, brand } = siteData;

  return (
    <footer className="bg-[#0A0A0A] text-white py-12">
      <div className="max-w-[1200px] mx-auto px-6 md:px-8">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8 mb-8">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#008055] flex items-center justify-center">
              <span className="text-white font-semibold text-sm">P2</span>
            </div>
            <span className="text-white font-semibold text-lg tracking-tight">
              {brand.name}
            </span>
          </div>

          {/* Links */}
          <div className="flex items-center gap-6">
            {footer.links.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="text-[#AFADB2] hover:text-white text-sm no-underline transition-colors duration-200"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 pt-6">
          <p className="text-[#757478] text-xs leading-relaxed max-w-[700px]">
            {footer.disclaimer}
          </p>
          <p className="text-[#757478] text-xs mt-3">
            © {new Date().getFullYear()} {brand.name}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
